import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import { updateUser } from '../slices/authSlice';
import { 
    UserIcon, 
    EnvelopeIcon, 
    LockClosedIcon, 
    CurrencyDollarIcon,
    SparklesIcon,
    CheckCircleIcon,
    ExclamationCircleIcon,
    EyeIcon,
    EyeSlashIcon,
    CogIcon,
    ShieldCheckIcon
} from '@heroicons/react/24/outline'; 
import UserCredits from '../components/UserCredits'; 
import { useNavigate } from 'react-router-dom';
import useApiWithAuth from '../utils/useApiWithAuth';

const Profile = () => {
    const { apiCall } = useApiWithAuth();
    const [formData, setFormData] = useState({ username: '', email: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const regexEmail = /^[^@\s]{2,30}@[^@\s]{2,30}\.[^@\s]{2,5}$/;
    const regexPassword = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/;

    const validateEmail = (email) => {
        if (!regexEmail.test(email)) {
            setEmailError("L'adresse email n'est pas valide.");
        } else {
            setEmailError('');
        }
    };

    const validatePassword = (password) => {
        if (password && !regexPassword.test(password)) {
            setPasswordError('Le mot de passe doit comporter au moins 8 caractères, avec au moins une majuscule, une minuscule, un chiffre et un symbole.');
        } else {
            setPasswordError('');
        }
    };

    useEffect(() => {
        const fetchProfile = async () => {
            setLoading(true);
            try {
                const response = await apiCall('https://cvanalyzerback.osc-fr1.scalingo.io/api/users/profile');
                const data = await response.json();
                setFormData({ username: data.username, email: data.email, password: '' });
            } catch (err) {
                setError(err.message);
                // Si c'est une erreur d'authentification, rediriger vers login
                if (err.message.includes('Session expirée') || err.message.includes('401')) {
                    navigate('/login');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, [apiCall, navigate]);

    const onChange = e => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setSuccess(false);
        
        // Supprimer les erreurs quand l'utilisateur tape
        if (e.target.name === 'email' && emailError) {
            setEmailError('');
        }
        if (e.target.name === 'password' && passwordError) {
            setPasswordError('');
        }
    };

    const onBlur = e => {
        // Valider seulement quand l'utilisateur quitte le champ
        if (e.target.name === 'email' && e.target.value) {
            validateEmail(e.target.value);
        }
        if (e.target.name === 'password' && e.target.value) {
            validatePassword(e.target.value);
        }
    };

    const onSubmit = async e => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(false);

        if (emailError || passwordError) {
            setLoading(false);
            return;
        }

        try {
            await apiCall('https://cvanalyzerback.osc-fr1.scalingo.io/api/users/profile', {
                method: 'PUT',
                body: JSON.stringify(formData)
            });
            
            setSuccess(true);
            dispatch(updateUser({ username: formData.username, email: formData.email }));
            setFormData({ ...formData, password: '' }); // Vider le champ mot de passe
        } catch (err) {
            setError(err.message);
            // Si c'est une erreur d'authentification, rediriger vers login
            if (err.message.includes('Session expirée') || err.message.includes('401')) {
                navigate('/login');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 pt-16 p-4">
            <Helmet>
                <title>Mon profil | CV Analyse</title>
                <meta name="robots" content="noindex, nofollow" />
                <link rel="canonical" href="https://www.cv-analyse.fr/profile" />
            </Helmet>
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="text-center mb-12">
                    <div className="flex items-center justify-center mb-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center mr-3">
                            <SparklesIcon className="w-6 h-6 text-white" />
                        </div>
                        <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                            Mon Profil
                        </h1>
                    </div>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                        Gérez vos informations personnelles et vos paramètres de compte
                    </p>
                </div>

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Section Crédits */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">
                            <div className="flex items-center mb-6">
                                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-xl flex items-center justify-center mr-4">
                                    <CurrencyDollarIcon className="w-6 h-6 text-white" />
                                </div>
                                <h2 className="text-2xl font-bold text-gray-800">Mes Crédits</h2>
                            </div>
                            <UserCredits />
                            <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl border border-blue-100">
                                <p className="text-sm text-gray-600 text-center">
                                    Utilisez vos crédits pour analyser vos CV et obtenir des suggestions d'amélioration
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Section Formulaire */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">
                            <div className="flex items-center mb-8">
                                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mr-4">
                                    <CogIcon className="w-6 h-6 text-white" />
                                </div>
                                <h2 className="text-2xl font-bold text-gray-800">Informations du compte</h2>
                            </div>

                            {loading && (
                                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center mb-6">
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3"></div>
                                    <span className="text-blue-600">Mise à jour en cours...</span>
                                </div>
                            )}

                            {error && (
                                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center mb-6">
                                    <ExclamationCircleIcon className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
                                    <span className="text-red-600 text-sm">{error}</span>
                                </div>
                            )}

                            {success && (
                                <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center mb-6">
                                    <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                                    <span className="text-green-600 text-sm">Profil mis à jour avec succès !</span>
                                </div>
                            )}

                            <form onSubmit={onSubmit} className="space-y-6">
                                {/* Nom d'utilisateur */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center">
                                        <UserIcon className="w-4 h-4 mr-2 text-blue-600" />
                                        Nom d'utilisateur
                                    </label>
                                    <input
                                        type="text"
                                        name="username"
                                        value={formData.username}
                                        onChange={onChange}
                                        required
                                        className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white"
                                        placeholder="Votre nom d'utilisateur"
                                    />
                                </div>

                                {/* Email */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center">
                                        <EnvelopeIcon className="w-4 h-4 mr-2 text-blue-600" />
                                        Adresse email
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={onChange}
                                        onBlur={onBlur}
                                        required
                                        className={`w-full px-4 py-4 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white ${
                                            emailError ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="exemple@email.com"
                                    />
                                    {emailError && (
                                        <div className="mt-2 flex items-center text-red-500 text-sm">
                                            <ExclamationCircleIcon className="w-4 h-4 mr-2 flex-shrink-0" />
                                            <span>{emailError}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Mot de passe */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center">
                                        <LockClosedIcon className="w-4 h-4 mr-2 text-blue-600" />
                                        Nouveau mot de passe
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            value={formData.password}
                                            onChange={onChange}
                                            onBlur={onBlur}
                                            className={`w-full px-4 py-4 pr-12 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white ${
                                                passwordError ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="Laissez vide pour ne pas changer"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                        >
                                            {showPassword ? (
                                                <EyeSlashIcon className="w-5 h-5" />
                                            ) : (
                                                <EyeIcon className="w-5 h-5" />
                                            )}
                                        </button>
                                    </div>
                                    {passwordError && (
                                        <div className="mt-2 flex items-start text-red-500 text-sm">
                                            <ExclamationCircleIcon className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                                            <span>{passwordError}</span>
                                        </div>
                                    )}
                                    <p className="mt-2 text-xs text-gray-500">
                                        Laissez ce champ vide si vous ne souhaitez pas changer votre mot de passe
                                    </p>
                                </div>

                                {/* Bouton de soumission */}
                                <button
                                    type="submit"
                                    disabled={loading || emailError || passwordError}
                                    className={`w-full py-4 px-6 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center ${
                                        loading || emailError || passwordError
                                            ? 'bg-gray-400 cursor-not-allowed'
                                            : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-1'
                                    }`}
                                >
                                    {loading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                                            Mise à jour en cours...
                                        </>
                                    ) : (
                                        <>
                                            <ShieldCheckIcon className="w-5 h-5 mr-2" />
                                            Mettre à jour le profil
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;
